import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: null,
    videos: [],
    quizzes: [],
    users: [],
    categories: [],
    inspectItems: [],
    studyRecords: []
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_VIDEOS(state, videos) {
      state.videos = videos
    },
    SET_QUIZZES(state, quizzes) {
      state.quizzes = quizzes
    },
    SET_USERS(state, users) {
      state.users = users
    },
    SET_INSPECT_ITEMS(state, inspectItems) {
      state.inspectItems = inspectItems
    },
    SET_STUDY_RECORDS(state, studyRecords) {
      state.studyRecords = studyRecords
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    ADD_VIDEO(state, video) {
      state.videos.push(video)
    },
    UPDATE_VIDEO(state, { id, video }) {
      const index = state.videos.findIndex(v => v.id === id)
      if (index !== -1) {
        state.videos.splice(index, 1, { ...state.videos[index], ...video })
      }
    },
    DELETE_VIDEO(state, id) {
      state.videos = state.videos.filter(v => v.id !== id)
    },
    ADD_QUIZ(state, quiz) {
      state.quizzes.push(quiz)
    },
    UPDATE_QUIZ(state, { id, quiz }) {
      const index = state.quizzes.findIndex(q => q.id === id)
      if (index !== -1) {
        state.quizzes.splice(index, 1, { ...state.quizzes[index], ...quiz })
      }
    },
    DELETE_QUIZ(state, id) {
      state.quizzes = state.quizzes.filter(q => q.id !== id)
    },
    ADD_USER(state, user) {
      state.users.push(user)
    },
    UPDATE_USER(state, { id, user }) {
      const index = state.users.findIndex(u => u.id === id)
      if (index !== -1) {
        state.users.splice(index, 1, { ...state.users[index], ...user })
      }
    },
    DELETE_USER(state, id) {
      state.users = state.users.filter(u => u.id !== id)
    },
    UPDATE_INSPECT_ITEM(state, { id, item }) {
      const index = state.inspectItems.findIndex(i => i.id === id)
      if (index !== -1) {
        state.inspectItems.splice(index, 1, { ...state.inspectItems[index], ...item })
      }
    },
    ADD_INSPECT_ITEM(state, item) {
      state.inspectItems.push(item)
    },
    DELETE_INSPECT_ITEM(state, id) {
      state.inspectItems = state.inspectItems.filter(i => i.id !== id)
    }
  },
  actions: {
    // 管理员信息
    async fetchAdminInfo({ commit }) {
      const { api } = await import('@/services/api')
      const response = await api.admin.info()
      if (response.code === 200) {
        commit('SET_USER', response.data)
        return response.data
      }
      throw new Error(response.message)
    },

    // 栏目相关
    async fetchCategories({ commit }) {
      const { api } = await import('@/services/api')
      const response = await api.category.list()
      if (response.code === 200) {
        commit('SET_CATEGORIES', response.data.list || response.data || [])
        return response.data.list || response.data || []
      }
      throw new Error(response.message)
    },
    async addCategory({ commit }, category) {
      const { api } = await import('@/services/api')
      const response = await api.category.create(category)
      if (response.code === 200) {
        commit('SET_CATEGORIES', [...this.state.categories, response.data])
        return response.data
      }
      throw new Error(response.message)
    },
    async updateCategory({ commit }, payload) {
      const { api } = await import('@/services/api')
      const response = await api.category.update(payload.id, payload.category)
      if (response.code === 200) {
        const list = this.state.categories.map(c => c.id === payload.id ? response.data : c)
        commit('SET_CATEGORIES', list)
        return response.data
      }
      throw new Error(response.message)
    },
    async deleteCategory({ commit }, id) {
      const { api } = await import('@/services/api')
      const response = await api.category.delete(id)
      if (response.code === 200) {
        commit('SET_CATEGORIES', this.state.categories.filter(c => c.id !== id))
      } else {
        throw new Error(response.message)
      }
    },

    // 视频相关
    async fetchVideos({ commit }) {
      const { api } = await import('@/services/api')
      const response = await api.video.list()
      if (response.code === 200) {
        const videos = response.data.list || response.data || []
        // 处理数据格式转换（snake_case -> camelCase）
        const processedVideos = videos.map(video => ({
          ...video,
          videoUrl: video.video_url,
          pdfImages: video.pdf_images,
          createdAt: video.created_at,
          updatedAt: video.updated_at
        }))
        commit('SET_VIDEOS', processedVideos)
        return processedVideos
      }
      throw new Error(response.message)
    },
    async addVideo({ commit }, video) {
      const { api } = await import('@/services/api')
      const response = await api.video.create(video)
      if (response.code === 200) {
        const newVideo = {
          ...response.data,
          videoUrl: response.data.video_url,
          pdfImages: response.data.pdf_images,
          createdAt: response.data.created_at,
          updatedAt: response.data.updated_at
        }
        commit('ADD_VIDEO', newVideo)
        return newVideo
      }
      throw new Error(response.message)
    },
    async updateVideo({ commit }, payload) {
      const { api } = await import('@/services/api')
      const response = await api.video.update(payload.id, payload.video)
      if (response.code === 200) {
        const updatedVideo = {
          ...response.data,
          videoUrl: response.data.video_url,
          pdfImages: response.data.pdf_images,
          createdAt: response.data.created_at,
          updatedAt: response.data.updated_at
        }
        commit('UPDATE_VIDEO', { id: payload.id, video: updatedVideo })
        return updatedVideo
      }
      throw new Error(response.message)
    },
    async deleteVideo({ commit }, id) {
      const { api } = await import('@/services/api')
      const response = await api.video.delete(id)
      if (response.code === 200) {
        commit('DELETE_VIDEO', id)
      } else {
        throw new Error(response.message)
      }
    },
    
    // 测验题相关
    async fetchQuizzes({ commit }, params) {
      const { api } = await import('@/services/api')
      const response = await api.quiz.list(params)
      if (response.code === 200) {
        const quizzes = response.data.list || response.data || []
        // 处理数据格式转换（snake_case -> camelCase）
        const processedQuizzes = quizzes.map(quiz => ({
          ...quiz,
          videoId: quiz.video_id,
          correctIndex: quiz.correct_index
        }))
        commit('SET_QUIZZES', processedQuizzes)
        return processedQuizzes
      }
      throw new Error(response.message)
    },
    async addQuiz({ commit }, quiz) {
      const { api } = await import('@/services/api')
      const response = await api.quiz.create(quiz)
      if (response.code === 200) {
        // 处理数据格式转换（snake_case -> camelCase）
        const newQuiz = {
          ...response.data,
          videoId: response.data.video_id,
          correctIndex: response.data.correct_index
        }
        commit('ADD_QUIZ', newQuiz)
        return newQuiz
      }
      throw new Error(response.message)
    },
    async updateQuiz({ commit }, payload) {
      const { api } = await import('@/services/api')
      const response = await api.quiz.update(payload.id, payload.quiz)
      if (response.code === 200) {
        // 处理数据格式转换（snake_case -> camelCase）
        const updatedQuiz = {
          ...response.data,
          videoId: response.data.video_id,
          correctIndex: response.data.correct_index
        }
        commit('UPDATE_QUIZ', { id: payload.id, quiz: updatedQuiz })
        return updatedQuiz
      }
      throw new Error(response.message)
    },
    async deleteQuiz({ commit }, id) {
      const { api } = await import('@/services/api')
      const response = await api.quiz.delete(id)
      if (response.code === 200) {
        commit('DELETE_QUIZ', id)
      } else {
        throw new Error(response.message)
      }
    },
    
    // 用户相关
    async fetchUsers({ commit }, params) {
      const { api } = await import('@/services/api')
      const response = await api.user.list(params)
      if (response.code === 200) {
        commit('SET_USERS', response.data.list || response.data)
        return response.data.list || response.data
      }
      throw new Error(response.message)
    },
    async addUser({ commit }, user) {
      const { api } = await import('@/services/api')
      const response = await api.user.create(user)
      if (response.code === 200) {
        commit('ADD_USER', response.data)
        return response.data
      }
      throw new Error(response.message)
    },
    async updateUser({ commit }, payload) {
      const { api } = await import('@/services/api')
      const response = await api.user.update(payload.id, payload.user)
      if (response.code === 200) {
        commit('UPDATE_USER', payload)
        return response.data
      }
      throw new Error(response.message)
    },
    async deleteUser({ commit }, id) {
      const { api } = await import('@/services/api')
      const response = await api.user.delete(id)
      if (response.code === 200) {
        commit('DELETE_USER', id)
      } else {
        throw new Error(response.message)
      }
    },
    
    // 学习记录相关
    async fetchStudyRecords({ commit }, params) {
      const { api } = await import('@/services/api')
      const response = await api.studyRecord.list(params)
      if (response.code === 200) {
        const records = response.data.list || response.data || []
        // 处理数据格式转换（snake_case -> camelCase）
        const processedRecords = records.map(record => ({
          ...record,
          userId: record.user_id,
          userName: record.user ? record.user.name : null,
          videoId: record.video_id,
          categoryId: record.category_id,
          videoTitle: record.video ? record.video.title : (record.video_title || ''),
          watched: parseInt(record.watched) === 1,
          completed: parseInt(record.completed) === 1,
          progress: parseInt(record.progress) || 0,
          quizCorrect: record.quiz_correct !== null ? (parseInt(record.quiz_correct) === 1) : null,
          watchTime: record.watch_time,
          completeTime: record.complete_time,
          signature: record.signature
        }))
        commit('SET_STUDY_RECORDS', processedRecords)
        return processedRecords
      }
      throw new Error(response.message)
    },
    
    // 隐患排查相关
    async fetchInspectItems({ commit }, params) {
      const { api } = await import('@/services/api')
      const response = await api.inspectItem.list(params)
      if (response.code === 200) {
        // 后端返回 { list: [...], total: ... }
        const items = response.data.list || response.data || []
        // 处理数据格式，将 assignedUser.name 映射到 assignedUserName
        const processedItems = items.map(item => ({
          ...item,
          assignedUserId: item.assigned_user_id,
          assignedUserName: item.assignedUser ? item.assignedUser.name : null,
          photoUrl: item.photo_url,
          completedTime: item.completed_time
        }))
        commit('SET_INSPECT_ITEMS', processedItems)
        return processedItems
      }
      throw new Error(response.message)
    },
    async updateInspectItem({ commit }, payload) {
      const { api } = await import('@/services/api')
      const response = await api.inspectItem.update(payload.id, payload.item)
      if (response.code === 200) {
        // 处理返回数据格式
        const item = response.data
        const processedItem = {
          ...item,
          assignedUserId: item.assigned_user_id,
          assignedUserName: item.assignedUser ? item.assignedUser.name : null,
          photoUrl: item.photo_url,
          completedTime: item.completed_time
        }
        commit('UPDATE_INSPECT_ITEM', { id: payload.id, item: processedItem })
        return processedItem
      }
      throw new Error(response.message)
    },
    async addInspectItem({ commit }, item) {
      const { api } = await import('@/services/api')
      const response = await api.inspectItem.create(item)
      if (response.code === 200) {
        // 处理返回数据格式
        const newItem = response.data
        const processedItem = {
          ...newItem,
          assignedUserId: newItem.assigned_user_id,
          assignedUserName: newItem.assignedUser ? newItem.assignedUser.name : null,
          photoUrl: newItem.photo_url,
          completedTime: newItem.completed_time
        }
        commit('ADD_INSPECT_ITEM', processedItem)
        return processedItem
      }
      throw new Error(response.message)
    },
    async deleteInspectItem({ commit }, id) {
      const { api } = await import('@/services/api')
      const response = await api.inspectItem.delete(id)
      if (response.code === 200) {
        commit('DELETE_INSPECT_ITEM', id)
      } else {
        throw new Error(response.message)
      }
    }
  },
  getters: {
    videoCount: state => state.videos.length,
    quizCount: state => state.quizzes.length,
    userCount: state => state.users.length,
    completedVideoCount: state => state.studyRecords.filter(record => record.completed).length,
    completedInspectCount: state => state.inspectItems.filter(item => item.completed).length,
    totalInspectCount: state => state.inspectItems.length,
    completedStudyCount: state => state.studyRecords.filter(record => record.completed).length,
    totalStudyCount: state => state.studyRecords.length
  }
})